import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, CheckSquare, Square, Download, Sparkles } from "lucide-react";
import { api, errMsg, fmtDateTime, openBlob } from "../lib/api";
import { UploadZone } from "../components/UploadZone";
import { PageTitle, Chip } from "../components/common";
import { PageViewer } from "../components/pm/PageViewer";
import { ReportCard } from "../components/pm/ReportCard";
import { LotQuestions } from "../components/pm/LotQuestions";
import { Lexicon } from "../components/pm/Lexicon";

function Summary({ s }) {
  const items = [["files", "Fichiers reçus"], ["pages", "Pages"], ["pages_examined", "Pages examinées"], ["reports", "Rapports identifiés"], ["annotations", "Annotations relevées"], ["open", "À clarifier"]];
  return (
    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="lot-summary">
      {items.map(([k, l]) => (
        <div key={k} className={`rounded-lg p-2 ${k === "open" && s[k] ? "bg-amber-100" : "bg-slate-100"}`}>
          <div className="text-xs text-slate-500">{l}</div><div className="text-2xl font-bold tl-mono" data-testid={`lot-summary-${k}`}>{s[k]}</div>
        </div>
      ))}
    </div>
  );
}

function LotList({ onOpen }) {
  const [lots, setLots] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/pm/lots").then((r) => setLots(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { const { data } = await api.post("/pm/lots", { name }); setName(""); onOpen(data.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="tl-card flex gap-2 flex-wrap">
        <input className="tl-input flex-1" placeholder="Nom du lot (ex. CRISPO semaine 37)" value={name} onChange={(e) => setName(e.target.value)} data-testid="lot-name-input" />
        <button className="tl-btn tl-btn-primary" onClick={create} disabled={!name.trim()} data-testid="lot-create-button"><Plus size={16} /> Nouveau lot</button>
      </div>
      <div className="tl-card overflow-x-auto">
        <table className="tl-table" data-testid="lots-table">
          <thead><tr><th>Lot</th><th>Créé</th><th>Fichiers</th><th>Pages examinées</th><th>Rapports</th><th>Annotations</th><th>À clarifier</th></tr></thead>
          <tbody>
            {lots.map((l) => (
              <tr key={l.id} className="cursor-pointer" onClick={() => onOpen(l.id)} data-testid={`lot-row-${l.id}`}>
                <td className="font-semibold">{l.name}</td><td className="text-xs">{fmtDateTime(l.created_at)}<div>{l.created_by}</div></td>
                <td>{l.summary.files}</td><td>{l.summary.pages_examined}/{l.summary.pages}</td><td>{l.summary.reports}</td><td>{l.summary.annotations}</td>
                <td>{l.summary.open ? <Chip tone="amber">{l.summary.open}</Chip> : <Chip tone="green">0</Chip>}</td>
              </tr>
            ))}
            {!lots.length && <tr><td colSpan={7} className="text-slate-500">Aucun lot. Créez-en un pour déposer des PM Reports.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Uploader({ lid, onDone }) {
  const [progress, setProgress] = useState("");
  const report = (r) => {
    if (r.error) toast.error(r.message);
    else if (r.duplicate) toast.warning(r.message);
    else toast.success(`${r.file.filename} : ${r.file.view === "other" ? "aperçu indisponible, fichier conservé" : `${r.file.page_count} page(s)`}`);
  };
  const upload = async (list) => {
    for (const [i, file] of list.entries()) {
      setProgress(`${i + 1}/${list.length}`);
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post(`/pm/lots/${lid}/files`, fd);
        (data.results || [data]).forEach(report);
      } catch (err) { toast.error(`${file.name} : ${errMsg(err)}`); }
    }
    setProgress("");
    onDone();
  };
  return <UploadZone onFiles={upload} label="Déposer des fichiers" testid="lot-upload" progress={progress} />;
}

function PageList({ files, pages, reports, sel, onSel }) {
  const examined = (fid, n) => pages.some((p) => p.file_id === fid && p.page_no === n && p.examined);
  const count = (fid, n) => reports.filter((r) => r.file_id === fid && r.page_no === n).length;
  return (
    <div className="space-y-3" data-testid="lot-page-list">
      {files.map((f) => (
        <div key={f.id}>
          <div className="text-sm font-semibold truncate" title={f.filename}>{f.filename}</div>
          {f.view === "other" && (
            <button className="text-xs underline flex items-center gap-1 mt-1" onClick={() => openBlob(`/files/${f.id}/download`, f.filename, true)} data-testid={`file-download-${f.id}`}>
              <Download size={12} /> Aperçu indisponible — télécharger
            </button>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {Array.from({ length: f.view === "other" ? 0 : f.page_count || 1 }, (_, i) => i + 1).map((n) => (
              <button key={n} onClick={() => onSel({ file: f, page: n })} data-testid={`page-btn-${f.id}-${n}`}
                className={`tl-btn tl-btn-sm ${sel?.file.id === f.id && sel.page === n ? "tl-btn-dark" : examined(f.id, n) ? "bg-emerald-100" : "tl-btn-ghost border border-slate-300"}`}>
                p.{n}{count(f.id, n) > 0 && <span className="text-[10px] ml-1">({count(f.id, n)})</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PagePanel({ lid, sel, data, companies, lexicon, reload }) {
  const pg = data.pages.find((p) => p.file_id === sel.file.id && p.page_no === sel.page);
  const reports = data.reports.filter((r) => r.file_id === sel.file.id && r.page_no === sel.page);
  const toggle = async () => {
    try { await api.put(`/pm/lots/${lid}/pages/${sel.file.id}/${sel.page}`, { examined: !pg?.examined }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const addReport = async () => {
    try { await api.post(`/pm/lots/${lid}/reports`, { file_id: sel.file.id, page_no: sel.page, printed: {}, annotations: [] }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="lg:sticky lg:top-4 self-start">
        <div className="text-sm mb-1"><b>{sel.file.filename}</b> · page {sel.page}/{sel.file.page_count}</div>
        <PageViewer fileId={sel.file.id} page={sel.page} />
        <button className="tl-btn tl-btn-ghost tl-btn-sm mt-2" onClick={toggle} data-testid="page-examined-toggle">
          {pg?.examined ? <CheckSquare size={16} className="text-emerald-700" /> : <Square size={16} />} Page examinée (bas de page et marges compris)
        </button>
      </div>
      <div className="space-y-3">
        {reports.map((r, i) => <ReportCard key={r.id} r={r} idx={i} companies={companies} lexicon={lexicon} onChanged={reload} />)}
        <button className="tl-btn tl-btn-primary w-full" onClick={addReport} data-testid="page-add-report"><Plus size={16} /> Rapport identifié sur cette page</button>
      </div>
    </div>
  );
}

function AiRead({ lot, onStart }) {
  const ai = lot.ai || {};
  const auto = async () => {
    try {
      const { data } = await api.post(`/pm/lots/${lot.id}/auto`);
      toast.success(`${data.rapports} rapport(s) traité(s) · ${data.rapproches} rapproché(s) · ${data.annotations_validees} annotation(s) claire(s) validée(s)`);
      onStart();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const start = async () => {
    if (!window.confirm("Lancer la lecture automatique (IA) de toutes les pages non encore lues ? Les propositions restent « à clarifier » jusqu'à votre validation. Coûte des crédits IA.")) return;
    try { await api.post(`/pm/lots/${lot.id}/ai-read`); toast.success("Lecture automatique lancée"); onStart(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap text-sm" data-testid="lot-ai">
      <button className="tl-btn tl-btn-dark tl-btn-sm" onClick={start} disabled={ai.status === "running"} data-testid="lot-ai-start">
        {ai.status === "running" ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Lecture automatique (IA)
      </button>
      <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={auto} disabled={ai.status === "running"} data-testid="lot-auto-process">Attribuer et rapprocher</button>
      {ai.auto && <span className="text-xs text-slate-500" data-testid="lot-ai-auto">Auto : {ai.auto.rapproches} rapproché(s), {ai.auto.annotations_validees} annotation(s) claire(s) validée(s)</span>}
      {ai.status && <span data-testid="lot-ai-progress">{ai.status === "running" ? `Lecture en cours : ${ai.done}/${ai.total} pages` : ai.status === "budget" ? `Arrêtée : solde de crédits IA épuisé (${ai.done}/${ai.total}) — rechargez puis relancez` : ai.status === "interrupted" ? `Interrompue (${ai.done}/${ai.total}) — relancez pour lire les pages restantes` : `Dernière lecture : ${ai.done}/${ai.total} pages`}{ai.errors?.length ? ` · ${ai.errors.length} page(s) non lue(s)` : ""}</span>}
    </div>
  );
}

function LotView({ lid, onBack }) {
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [lexicon, setLexicon] = useState([]);
  const [sel, setSel] = useState(null);
  const [tab, setTab] = useState("pages");
  const reload = useCallback(() => api.get(`/pm/lots/${lid}`).then((r) => setData(r.data)), [lid]);
  const loadLex = () => api.get("/pm/lexicon").then((r) => setLexicon(r.data));
  useEffect(() => {
    reload();
    loadLex();
    api.get("/companies").then((r) => setCompanies(r.data));
  }, [reload]);
  const running = data?.lot.ai?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(reload, 4000);
    return () => clearInterval(t);
  }, [running, reload]);
  if (!data) return <Loader2 className="animate-spin" />;
  const openQ = data.reports.reduce((n, r) => n + (r.questions || []).filter((q) => q.status !== "answered").length, 0);
  return (
    <div className="space-y-4" data-testid="lot-view">
      <div className="flex items-center gap-2 flex-wrap">
        <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={onBack} data-testid="lot-back"><ArrowLeft size={16} /> Lots</button>
        <h2 className="text-2xl font-bold uppercase" data-testid="lot-title">{data.lot.name}</h2>
      </div>
      <Uploader lid={lid} onDone={reload} />
      <AiRead lot={data.lot} onStart={reload} />
      <Summary s={data.summary} />
      <div className="flex gap-1">
        {[["pages", "Pages et rapports"], ["questions", `Questions du lot (${openQ})`], ["lexicon", "Lexique métier"]].map(([k, l]) => (
          <button key={k} className={`tl-btn tl-btn-sm ${tab === k ? "tl-btn-dark" : "tl-btn-ghost"}`} onClick={() => setTab(k)} data-testid={`lot-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "pages" && (
        <div className="grid lg:grid-cols-[220px_1fr] gap-4">
          <div className="tl-card"><PageList files={data.files} pages={data.pages} reports={data.reports} sel={sel} onSel={setSel} />
            {!data.files.length && <p className="text-sm text-slate-500">Déposez des fichiers, un dossier entier ou un ZIP : scans, photos (y compris iPhone HEIC), PDF, Word, Excel…</p>}</div>
          {sel ? <PagePanel lid={lid} sel={sel} data={data} companies={companies} lexicon={lexicon} reload={reload} /> : <p className="text-sm text-slate-500">Choisissez une page à examiner.</p>}
        </div>
      )}
      {tab === "questions" && <LotQuestions reports={data.reports} onChanged={reload} />}
      {tab === "lexicon" && <Lexicon items={lexicon} onChanged={loadLex} />}
    </div>
  );
}

export default function PmReports() {
  const [params, setParams] = useSearchParams();
  const lot = params.get("lot");
  return (
    <div>
      <PageTitle title="PM Reports" sub="Dépôt, lecture des annotations manuscrites, rapprochement et validation" />
      {lot ? <LotView lid={lot} onBack={() => setParams({})} /> : <LotList onOpen={(id) => setParams({ lot: id })} />}
    </div>
  );
}
